/* =============================================================================
 * FilterRangeSection — shared filter primitive (KGH-795)
 * -----------------------------------------------------------------------------
 * A FilterSection whose body is the accessibility-complete RangeSlider. Three
 * of the coach's five metrics are times measured in hundredths, so `step` is
 * explicit at every call site: the slider's own default of 1 leaves a 2.6-5.0
 * range with four usable positions.
 *
 * A metric no athlete in the pool has comes back with null bounds. That renders
 * as a disabled section carrying the reason, never as a missing section — a
 * control that silently disappears reads as a broken page.
 * ========================================================================== */
import React from 'react';
import RangeSlider from '../RangeSlider';
import { FilterSection } from './FilterSection';

export interface FilterRangeSectionProps {
  title: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  active?: boolean;
  id?: string;
  /** The hairline + padding below the section. False on the last one. */
  divider?: boolean;
  min: number | null;
  max: number | null;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatLabel?: (value: number) => string;
  step?: number;
  ariaLabel?: string;
  /** Bump to force the slider to remount (the Clear-filters reset). */
  resetKey?: number | string;
  /** Shown in place of the slider when the range has no bounds. */
  unavailableNote?: string;
}

export const FilterRangeSection: React.FC<FilterRangeSectionProps> = ({
  title,
  expanded,
  onToggle,
  active = false,
  id,
  divider = true,
  min,
  max,
  value,
  onChange,
  formatLabel,
  step = 1,
  ariaLabel,
  resetKey,
  unavailableNote = 'No athlete in this pool has this measurement on file.',
}) => {
  const unavailable = min === null || max === null || min === max;
  return (
    <FilterSection title={title} expanded={expanded} onToggle={onToggle} active={active} id={id} divider={divider}>
      {unavailable ? (
        <div style={{ fontSize: '13px', color: 'var(--aps-control-muted)', padding: '8px 0', lineHeight: 1.4 }}>
          {unavailableNote}
        </div>
      ) : (
        <RangeSlider
          key={resetKey === undefined ? undefined : `${id ?? 'range'}-${resetKey}`}
          min={min as number}
          max={max as number}
          value={value}
          onChange={onChange}
          formatLabel={formatLabel}
          step={step}
          ariaLabel={ariaLabel}
        />
      )}
    </FilterSection>
  );
};

export default FilterRangeSection;
